import { useEffect, useRef, useState } from 'react';
import { useActions } from '@/store/selectors';

interface Props {
  open: boolean;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
  title?: string;
}

/** Override reason capture modal — required reason textarea (PSD §8.4 / TODO Task 1.27). */
export function OverrideReasonModal({ open, onSubmit, onCancel, title = 'Document Override' }: Props): JSX.Element | null {
  const [reason, setReason] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const actions = useActions();

  useEffect(() => {
    if (open) {
      setReason('');
      textareaRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  const canSubmit = reason.trim().length > 0;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal modal-sm" role="document">
        <h2 className="modal-title">{title}</h2>
        <p className="modal-text">
          A reason is required to proceed. This will be recorded in the audit trail.
        </p>
        <div className="field">
          <label htmlFor="override-reason">Reason for override</label>
          <textarea
            id="override-reason"
            ref={textareaRef}
            className="modal-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            data-testid="override-reason-input"
          />
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="btn"
            disabled={!canSubmit}
            onClick={() => {
              onSubmit(reason.trim());
              void actions.logAuditEvent({ eventType: 'override', newValue: reason.trim() });
            }}
            data-testid="override-submit"
          >
            Submit override
          </button>
        </div>
      </div>
    </div>
  );
}
